import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  login as apiLogin,
  fetchMe,
  isMfaChallenge,
  logout as apiLogout,
  sendMfaSmsCode,
  switchPortal as apiSwitchPortal,
  verifyMfaCode,
  type AuthUser,
  type LoginResponse,
  type MfaChallenge,
} from './api';
import { clearToken, getToken, setToken } from './tokenStore';

interface AuthContextValue {
  /** Hydrated current user. `null` while loading or when unauthenticated. */
  user: AuthUser | null;
  /** True only during the initial token-validation fetch on mount. */
  loading: boolean;
  /** Sign in. Resolves on success, throws on failure.
   *  `remember` (default true) controls JWT TTL: true → 14 days, false → 1 day.
   *  S-1: for an MFA-enrolled account this resolves to an MfaChallenge
   *  instead. No token is stored, and the login page must collect a code
   *  and call completeMfaSignIn to finish.
   *  S-2: the challenge also carries mfa_methods ({ totp, sms }), which
   *  rides along on the same object as mfa_token and gets consumed by the
   *  same component. The provider does not interpret it: which inputs to
   *  show is a question about the code step, and the code step owns it. */
  signIn: (email: string, password: string, remember?: boolean) => Promise<LoginResponse | MfaChallenge>;
  /** S-1: step two of an MFA login. Verifies the code, then stores the
   *  session exactly the way signIn does on the password-only path. */
  completeMfaSignIn: (mfaToken: string, code: string) => Promise<LoginResponse>;
  /** S-2: mid-login, text a code to the number on file. Lives here beside
   *  completeMfaSignIn because it is part of the same challenge lifecycle
   *  and takes the same pending token; it creates no session of its own,
   *  so there is deliberately no state change here. Resolves with the
   *  masked destination for the confirmation line. */
  sendMfaSms: (mfaToken: string) => Promise<{ ok: true; sent_to_masked: string }>;
  /** Sign out and clear local token. */
  signOut: () => Promise<void>;
  /** Re-fetch the current user (after password change, profile update). */
  refresh: () => Promise<void>;
  /** Update the in-memory user (used after change-password flips the flag). */
  patchUser: (patch: Partial<AuthUser>) => void;
  /** Switch to another portal this user has access to (agent ↔ partner ↔
   *  client). Re-mints the token and swaps the in-memory user. */
  switchToPortal: (portal: string, teamId?: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(() => Boolean(getToken()));

  // On mount: if we have a token, hydrate the user from /me.
  useEffect(() => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (cancelled) return;
        setUser({
          id: me.id,
          email: me.email,
          first_name: me.first_name,
          last_name: me.last_name,
          must_change_password: me.must_change_password,
          team_id: me.team_id,
          roles: me.roles,
          primary_role: me.primary_role,
          portal: me.portal,
          phone: me.phone,
          email_signature: me.email_signature,
          is_tc: me.is_tc,
          coach: me.coach,
          briefing_focus: me.briefing_focus,
          beta_new_deal_ui: me.beta_new_deal_ui,
        });
      })
      .catch(() => {
        clearToken();
        setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string, remember = true) => {
    const result = await apiLogin(email, password, remember);
    // S-1 (Daniel 2026-08-04): a challenge is NOT a session. Store nothing
    // and hand it back. The login page runs the code step, then
    // completeMfaSignIn below does the storing.
    // S-2: handed back WHOLE, so mfa_methods reaches the login page with the
    // token it belongs to. Picking fields apart here is how the token and
    // the description of what it unlocks end up out of sync.
    if (isMfaChallenge(result)) {
      return result;
    }
    setToken(result.access_token);
    setUser(result.user);
    return result;
  }, []);

  // S-1: finish an MFA login. On success the API returns the exact same
  // payload shape as POST /login, so session storage is identical to the
  // password-only path above.
  const completeMfaSignIn = useCallback(async (mfaToken: string, code: string) => {
    const result = await verifyMfaCode(mfaToken, code);
    setToken(result.access_token);
    setUser(result.user);
    return result;
  }, []);

  // S-2: no setToken/setUser here on purpose. Requesting a text does not
  // authenticate anybody; it only sends a code. The session still comes
  // from completeMfaSignIn above.
  const sendMfaSms = useCallback((mfaToken: string) => sendMfaSmsCode(mfaToken), []);

  const signOut = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      /* Token may already be invalid — fine. */
    }
    clearToken();
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await fetchMe();
      setUser({
        id: me.id,
        email: me.email,
        first_name: me.first_name,
        last_name: me.last_name,
        must_change_password: me.must_change_password,
        team_id: me.team_id,
        roles: me.roles,
        primary_role: me.primary_role,
        portal: me.portal,
        phone: me.phone,
        email_signature: me.email_signature,
        is_tc: me.is_tc,
        coach: me.coach,
        briefing_focus: me.briefing_focus,
        beta_new_deal_ui: me.beta_new_deal_ui,
      });
    } catch {
      clearToken();
      setUser(null);
    }
  }, []);

  const patchUser = useCallback((patch: Partial<AuthUser>) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const switchToPortal = useCallback(async (portal: string, teamId?: number) => {
    const res = await apiSwitchPortal(portal, teamId);
    setToken(res.access_token);
    setUser(res.user);
    // Hard reload so every cached query refetches under the new portal's
    // permissions — cheaper and safer than invalidating every key.
    window.location.href = '/';
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      signIn,
      completeMfaSignIn,
      sendMfaSms,
      signOut,
      refresh,
      patchUser,
      switchToPortal,
    }),
    [
      user,
      loading,
      signIn,
      completeMfaSignIn,
      sendMfaSms,
      signOut,
      refresh,
      patchUser,
      switchToPortal,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
